import React, { useState } from 'react';

import {
  Apps as AppsIcon,
  Clear as ClearIcon,
  MoreHoriz as MoreHorizIcon,
} from '@mui/icons-material';
import { Chip } from '@mui/material';
import classNames from 'classnames';

import { Box } from '../../Box';
import { Tooltip } from '../../Tooltip';
import { Typography } from '../../Typography';
import { EllipsisText } from '../../EllipsisText';

import { useStyles } from './SelectedOptionsInput.styles';

/**
 * Интерфейс, определяющий структуру Option.
 * @typedef {Object} Option
 * @property {string} label - Надпись опции.
 * @property {string} value - Значение опции.
 * @property {{title: string, subtitle?: string} | undefined} [tooltip] - Необязательная информация для всплывающей подсказки.
 * @property {string | undefined} [prefix] - Необязательный префикс для отображения перед надписью опции.
 * @property {boolean | undefined} [error] - Указывает, находится ли опция в состоянии ошибки.
 */

interface Option {
  label: string;
  value: string;
  tooltip?: {
    title: string;
    subtitle?: string;
  };
  prefix?: string;
  error?: boolean;
}

/**
 * Интерфейс, определяющий свойства компонента SelectedOptionsInput.
 * @typedef {Object} SelectedOptionsInputProps
 * @property {Option[]} options - Список выбранных опций для отображения.
 * @property {((option: Option) => void) | undefined} [onDelete] - Функция обратного вызова, которая вызывается при удалении опции.
 * @property {((option: Option) => React.ReactElement) | undefined} [renderTooltip] - Пользовательская функция рендеринга для всплывающей подсказки.
 * @property {number | undefined} [limit] - Максимальное количество опций для отображения на странице (по умолчанию 15).
 * @property {number | undefined} [maxWidth] - Максимальная ширина компонента.
 * @property {boolean | undefined} [withTooltip] - Указывает, следует ли отображать всплывающие подсказки для опций (по умолчанию false).
 * @property {boolean | undefined} [disabledOptions] - Флаг, указывающий, отключены опции или нет (по умолчанию false).
 * @property {boolean | undefined} [showAllOptionsButton] - Флаг, указывающий, следует ли отображать кнопку "Показать все".
 */
interface SelectedOptionsInputProps {
  options: Option[];
  onDelete?: (option: Option) => void;
  renderTooltip?: (option: Option) => React.ReactElement;
  limit?: number;
  maxWidth?: number | string;
  withTooltip?: boolean;
  withBorder?: boolean;
  disabledOptions?: boolean;
  showAllOptionsButton?: boolean;
}

/**
 * Компонент SelectedOptionsInput используется для отображения списка выбранных опций.
 * Он поддерживает удаление опций, всплывающие подсказки (tooltips) и постраничную навигацию.
 *
 * @component
 * @param {SelectedOptionsInputProps} props - Свойства для компонента.
 * @param {Option[]} props.options - Список выбранных опций для отображения.
 * @param {((option: Option) => void) | undefined} props.onDelete - Функция обратного вызова, которая вызывается при удалении опции.
 * @param {((option: Option) => React.ReactElement) | undefined} props.renderTooltip - Пользовательская функция рендеринга для всплывающей подсказки.
 * @param {number | undefined} props.limit - Максимальное количество опций для отображения на странице (по умолчанию 15).
 * @param {number | undefined} props.maxWidth - Максимальная ширина компонента.
 * @param {boolean | undefined} props.withTooltip - Флаг, указывающий, следует ли отображать всплывающие подсказки для опций (по умолчанию false).
 * @param {boolean | undefined} props.disabledOptions - Флаг, указывающий, отключены опции или нет (по умолчанию false).
 * @param {boolean | undefined} props.showAllOptionsButton - Флаг, указывающий, следует ли отображать кнопку "Показать все".
 * @returns {React.ReactElement} Отрендеренный компонент SelectedOptionsInput.
 */

export const SelectedOptionsInput = ({
  options,
  onDelete,
  limit = 15,
  renderTooltip,
  withTooltip,
  withBorder,
  maxWidth,
  disabledOptions,
  showAllOptionsButton = true,
}: SelectedOptionsInputProps) => {
  const { classes } = useStyles({ maxWidth });
  const [page, setPage] = useState(1);

  const shownOptions = options.slice(0, limit * page);

  const handleOptionDelete = (option: Option) => {
    onDelete?.(option);
  };

  return (
    <Box display="flex" flexDirection="column" gap="8">
      <Box display="flex" flexWrap="wrap" maxWidth="100%" gap="8">
        {shownOptions.map((option) => (
          <Box key={option.value} maxWidth="100%">
            <Tooltip
              disabled={!withTooltip}
              title={renderTooltip ? renderTooltip(option) : option.label}
              placement="top"
            >
              <Chip
                classes={{
                  root: classNames(
                    classes.chipInput,
                    withBorder && classes.withBorder,
                  ),
                  deleteIcon: option.error ? classes.iconError : '',
                }}
                label={
                  <EllipsisText rows={1}>
                    <Typography color="inherit" variant="body3">
                      {`${option.prefix ? `${option.prefix}, ` : ''}${
                        option.label
                      }`}
                    </Typography>
                  </EllipsisText>
                }
                onDelete={() => handleOptionDelete(option)}
                deleteIcon={!disabledOptions ? <ClearIcon /> : undefined}
                className={classNames(
                  classes.chip,
                  option.error && classes.chipError,
                  disabledOptions && classes.chipDisabled,
                )}
              />
            </Tooltip>
          </Box>
        ))}
      </Box>
      {options.length > limit && (
        <Box display="flex" gap="8">
          {options.length > shownOptions.length && (
            <Box
              className={classes.button}
              onClick={() => setPage((currentPage) => currentPage + 1)}
            >
              <Typography color="inherit" variant="body3">
                Показать еще
              </Typography>
              <MoreHorizIcon />
            </Box>
          )}
          {showAllOptionsButton && options.length > shownOptions.length && (
            <Box
              className={classes.button}
              onClick={() => setPage(Math.ceil(options.length / limit))}
            >
              <Typography color="inherit" variant="body3">
                Показать все {options.length}
              </Typography>
              <AppsIcon />
            </Box>
          )}
          {options.length <= shownOptions.length && options.length > limit && (
            <Box className={classes.button} onClick={() => setPage(1)}>
              <Typography color="inherit" variant="body3">
                Скрыть
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
